import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<"working" | "error">("working");
  const [message, setMessage] = useState<string>("Finalising sign-in...");
  const [fallbackDeepLink, setFallbackDeepLink] = useState<string | null>(null);

  const url = useMemo(() => new URL(window.location.href), []);

  useEffect(() => {
    const run = async () => {
      const isNativeReturn = url.searchParams.get("native") === "1";
      const code = url.searchParams.get("code");
      const errorDescription = url.searchParams.get("error_description");

      // This route is hosted (web). If the flow started from native, bounce into the app via deep-link.
      if (isNativeReturn) {
        // Honor the scheme the native client requested (native SwiftUI app sends
        // scheme=qualityflownative). Default to the legacy Capacitor scheme so the
        // existing WebView wrapper keeps working unchanged.
        //
        // SECURITY: the scheme param is attacker-controllable and we redirect to it
        // carrying the OAuth `code`. Restrict to a strict allowlist of our own native
        // schemes so a crafted callback URL cannot exfiltrate the code to an arbitrary
        // app/scheme (open-redirect / OAuth code exfiltration).
        // URL schemes are case-insensitive, so normalize before the allowlist check.
        const ALLOWED_SCHEMES = new Set(["qualflowsuite", "qualityflownative"]);
        const requested = (url.searchParams.get("scheme") || "qualflowsuite").toLowerCase();
        const requestedScheme = ALLOWED_SCHEMES.has(requested) ? requested : "qualflowsuite";
        const deepLink = new URL(`${requestedScheme}://auth/callback`);
        if (code) deepLink.searchParams.set("code", code);
        if (errorDescription) deepLink.searchParams.set("error_description", errorDescription);

        const deepLinkStr = deepLink.toString();
        setFallbackDeepLink(deepLinkStr);

        // Try automatic redirect first.
        setMessage(errorDescription ? "Sign-in failed. Returning to the app..." : "Returning to the app...");
        window.location.replace(deepLinkStr);
        return;
      }

      if (errorDescription) {
        setStatus("error");
        setMessage(decodeURIComponent(errorDescription));
        return;
      }

      // Normal web flow: exchange the code for a session and go home.
      try {
        const looksLikeGoogleAuthCode = !!code && /^4\//.test(code);
        if (looksLikeGoogleAuthCode) {
          setStatus("error");
          setMessage(
            "Sign-in is misconfigured: this callback received a Google authorisation code directly. Please check your backend Google sign-in settings (use the managed Google sign-in, or update your custom Google OAuth redirect URIs), then try again."
          );
          return;
        }

        if (code) {
          await supabase.auth.exchangeCodeForSession(code);
        }
        navigate("/", { replace: true });
      } catch (e: any) {
        setStatus("error");
        setMessage(e?.message ?? "Unable to complete sign-in.");
      }
    };

    void run();
  }, [navigate, url]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Sign-in</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{message}</p>

          {fallbackDeepLink && (
            <Button
              type="button"
              variant="secondary"
              className="mt-4 w-full"
              onClick={() => (window.location.href = fallbackDeepLink)}
            >
              Open app
            </Button>
          )}

          {status === "error" && !fallbackDeepLink && (
            <button
              className="mt-4 text-sm underline"
              onClick={() => navigate("/login", { replace: true })}
            >
              Back to login
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
